import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  AlertCircle,
  Lightbulb,
  Plus,
  UserPlus,
  UserMinus,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ArrowRight,
  Info,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { eventTeamApiService, EventTeamApiError } from '../../../services/eventTeamApi';
import type { EventItem, EventAssignmentRole } from '../../../types/eventTeam';
import { EVENT_ASSIGNMENT_ROLES } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventPriorityBadge } from '../EventPriorityBadge';
import { CreatorProfile } from '../../CreatorProfile';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

const PAGE_SIZE = 25;

export interface EventIdeasPanelProps {
  perms: EventTeamPerms;
}

/**
 * Idea Board (guide §1.2) — "people can see what they want to be assigned to".
 * Priority-ordered ideas with self-assign/unassign, quick creation, and lead approval.
 */
export const EventIdeasPanel = ({ perms }: EventIdeasPanelProps) => {
  const navigate = useNavigate();
  const { identity } = useAuth();
  const myUuid = identity?.raw?.custom?.minecraft_uuid;

  const [ideas, setIdeas] = useState<EventItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Event ids the caller is actively self/lead-assigned to (for button state). */
  const [myAssignedEventIds, setMyAssignedEventIds] = useState<Set<string>>(new Set());
  /** Caller is definitely not on the roster (self-assign 404 — guide §7.8). */
  const [notOnRoster, setNotOnRoster] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [roleDropdownId, setRoleDropdownId] = useState<string | null>(null);

  // Quick creation form
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newPriority, setNewPriority] = useState(5);
  const [creating, setCreating] = useState(false);

  const loadIdeas = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await eventTeamApiService.listIdeas(targetPage, PAGE_SIZE);
      setIdeas(res.results);
      setTotal(res.total);
      setPage(targetPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyAssignments = useCallback(async () => {
    if (!myUuid) return;
    try {
      const me = await eventTeamApiService.getTeamMemberByUuid(myUuid);
      const assignments = await eventTeamApiService.getTeamMemberAssignments(me.id, 'active');
      setMyAssignedEventIds(
        new Set(assignments.filter((a) => a.target_type === 'event').map((a) => a.target_id)),
      );
      setNotOnRoster(false);
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 404) {
        setNotOnRoster(true);
      }
    }
  }, [myUuid]);

  useEffect(() => {
    void loadIdeas(1);
    void loadMyAssignments();
  }, [loadIdeas, loadMyAssignments]);

  const handleSelfAssign = async (eventId: string, role?: EventAssignmentRole) => {
    setBusyId(eventId);
    setRoleDropdownId(null);
    try {
      await eventTeamApiService.selfAssign(eventId, role);
      setMyAssignedEventIds((prev) => new Set(prev).add(eventId));
      setNotOnRoster(false);
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 404) {
        // Guide §7.8: friendly CTA instead of an error state.
        setNotOnRoster(true);
      } else {
        alert(err instanceof Error ? err.message : 'Failed to self-assign');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleSelfUnassign = async (eventId: string) => {
    setBusyId(eventId);
    try {
      await eventTeamApiService.selfUnassign(eventId);
      setMyAssignedEventIds((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to un-self-assign');
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = async (eventId: string) => {
    const reason = prompt('Optional approval note (leave blank to skip):');
    if (reason === null) return;
    setBusyId(eventId);
    try {
      await eventTeamApiService.changeEventStatus(eventId, 'approved', reason || undefined);
      await loadIdeas(page);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve idea');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      // Status defaults to `idea` server-side.
      await eventTeamApiService.createEvent({
        title: newTitle.trim(),
        description: newDescription.trim(),
        category: newCategory.trim() || null,
        priority: newPriority,
      });
      setNewTitle('');
      setNewDescription('');
      setNewCategory('');
      setNewPriority(5);
      setShowCreate(false);
      await loadIdeas(1);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create idea');
    } finally {
      setCreating(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Idea Board"
        icon={Lightbulb}
        iconClassName="text-yellow-400"
        count={total}
        description="Priority-ordered ideas. Assign yourself to what you want to work on."
        className="mb-0 flex-wrap"
        actions={
          perms.canCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
              aria-expanded={showCreate}
            >
              <Plus className="w-4 h-4" />
              Quick Idea
            </button>
          ) : undefined
        }
      />

      {notOnRoster && (
        <div className="alert alert-warning" role="status">
          <Info />
          <span className="flex-1">Not on the roster. Ask an event admin to add you.</span>
        </div>
      )}

      {showCreate && (
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SectionHeader title="New event idea" className="mb-0" />
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Title (e.g. The Heist Event)"
            className="glass-input w-full text-sm"
            autoFocus
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description"
            className="glass-input w-full text-sm min-h-[70px]"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Category (e.g. minigame)"
              className="glass-input text-sm w-44"
            />
            <label className="text-xs text-glass-400 inline-flex items-center gap-2">
              Priority
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(Number(e.target.value))}
                className="glass-select text-sm"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    P{p}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex-1" />
            <button type="button" onClick={() => setShowCreate(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || !newTitle.trim()}
              className="btn-primary"
            >
              {creating ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Post Idea
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-2">
              <SkeletonLoader className="h-4 w-56 max-w-full" />
              <SkeletonLoader className="h-3 w-full max-w-md" />
              <div className="flex gap-2">
                <SkeletonLoader className="h-5 w-10 rounded-full" />
                <SkeletonLoader className="h-5 w-20 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      ) : ideas.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No ideas"
          description={perms.canCreate ? 'Post a quick idea to get the board started.' : undefined}
        />
      ) : (
        <div className="space-y-3">
          {ideas.map((idea) => {
            const assigned = myAssignedEventIds.has(idea.id);
            const busy = busyId === idea.id;
            return (
              <div key={idea.id} className="glass-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => navigate(`/event-team/events/${idea.id}`)}
                      className="text-sm font-semibold text-white hover:text-accent-300 transition-colors inline-flex items-center gap-1 text-left"
                    >
                      {idea.title}
                      <ArrowRight className="w-3.5 h-3.5 text-glass-500" />
                    </button>
                    {idea.description && (
                      <p className="text-sm text-glass-400 mt-1 line-clamp-2 whitespace-pre-wrap">
                        {idea.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-glass-500">
                      <EventPriorityBadge priority={idea.priority} />
                      {idea.category && (
                        <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{idea.category}</span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        by <CreatorProfile creatorId={idea.created_by} inline />
                      </span>
                      <span className="tabular-nums">{formatDistanceToNow(new Date(idea.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {perms.canCreate &&
                      (assigned ? (
                        <button
                          onClick={() => void handleSelfUnassign(idea.id)}
                          disabled={busy}
                          className="glass-button-secondary btn-xs text-red-300"
                          title="Remove yourself from this idea"
                        >
                          {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                          Un-assign me
                        </button>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => setRoleDropdownId(roleDropdownId === idea.id ? null : idea.id)}
                            disabled={busy}
                            className={cn('glass-button-sm', notOnRoster && 'opacity-60')}
                            aria-expanded={roleDropdownId === idea.id}
                            title={notOnRoster ? 'Not on the roster' : 'Assign yourself'}
                          >
                            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                            Self-assign
                          </button>
                          {roleDropdownId === idea.id && (
                            <div className="absolute top-full right-0 mt-1 glass-card overflow-hidden z-30 min-w-[140px] py-1">
                              <div className="px-3 py-1.5 eyebrow">
                                As role…
                              </div>
                              {EVENT_ASSIGNMENT_ROLES.map((role) => (
                                <button
                                  key={role}
                                  onClick={() => void handleSelfAssign(idea.id, role)}
                                  className="w-full text-left px-3 py-1.5 text-sm text-glass-200 hover:bg-glass-200/10 transition-colors capitalize"
                                >
                                  {role}
                                </button>
                              ))}
                              <button
                                onClick={() => void handleSelfAssign(idea.id)}
                                className="w-full text-left px-3 py-1.5 text-sm text-glass-400 hover:bg-glass-200/10 transition-colors border-t border-glass-200/10"
                              >
                                No specific role
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    {perms.canLead && (
                      <button
                        onClick={() => void handleApprove(idea.id)}
                        disabled={busy}
                        className="glass-button-secondary btn-xs text-green-300"
                        title="Approve this idea (moves it into the pipeline)"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void loadIdeas(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-icon"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void loadIdeas(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-icon"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
